import { useState } from "react";
import { ChevronDown, ChevronUp, Download, Copy } from "react-feather";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import Icon from "@mdi/react";
import { mdiCheckCircle } from "@mdi/js";

const DeviceIdsViewer = ({
  deviceIds,
  title,
  onRemove,
  defaultOpen = false,
  id,
}: {
  deviceIds: string[];
  title: string;
  onRemove?: (id: string) => void;
  defaultOpen?: boolean;
  id?: string;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const { showToast } = useToast();

  if (!deviceIds || deviceIds.length === 0) return null;

  const handleDownload = () => {
    const csvContent =
      "device_id\n" + deviceIds.map((id) => `"${id}"`).join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `device_ids_${deviceIds.length}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(deviceIds.join("\n"));
      showToast({
        type: ToastType.success,
        text: `${deviceIds.length} device IDs copied`,
      });
    } catch (err) {
      showToast({
        type: ToastType.error,
        text: "Failed to copy device IDs",
      });
    }
  };

  return (
    <div
      id={id}
      className="w-full min-w-0 max-w-full overflow-x-hidden text-xs bg-gray-50 border rounded-md"
    >
      {/* Header */}
      <div className="flex min-w-0 items-center justify-between px-3 py-2 bg-white rounded-md">
        <div
          id={id ? `${id}-toggle` : undefined}
          className="flex min-w-0 items-center gap-2 font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer"
          onClick={() => setOpen((v) => !v)}
        >
          {/* Scanned Devices ({deviceIds.length}) */}
          <div className="flex min-w-0 items-center gap-2 text-sm font-semibold text-green-700">
            <Icon path={mdiCheckCircle} size={0.7} />
            <span className="truncate">
              {title} ({deviceIds.length})
            </span>
          </div>
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>

        <div className="ml-2 shrink-0 flex items-center gap-3">
          {/* Copy */}
          <div className="relative group">
            <button
              id={id ? `${id}-copy` : undefined}
              type="button"
              onClick={handleCopy}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              aria-label="Copy device IDs"
            >
              <Copy size={14} />
            </button>

            <span
              className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-gray-800 px-2 py-0.5 text-[10px] text-white
        opacity-0 group-hover:opacity-100 transition"
            >
              Copy
            </span>
          </div>

          {/* Download */}
          <div className="relative group">
            <button
              id={id ? `${id}-download` : undefined}
              type="button"
              onClick={handleDownload}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              aria-label="Download device IDs as CSV"
            >
              <Download size={14} />
            </button>

            <span
              className="pointer-events-none absolute -top-7 left-1/2 -translate-x-1/2
        whitespace-nowrap rounded bg-gray-800 px-2 py-0.5 text-[10px] text-white
        opacity-0 group-hover:opacity-100 transition"
            >
              Download CSV
            </span>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {open && (
        <div className="max-h-48 overflow-y-auto overflow-x-hidden border-t px-3 py-2">
          <div className="flex flex-wrap gap-1">
            {deviceIds.map((deviceId, i) => (
              <span
                key={i}
                className="max-w-full break-all px-2 py-0.5 border rounded bg-white font-mono flex items-center gap-1"
              >
                {deviceId}
                {onRemove && (
                  <button
                    id={id ? `${id}-remove-${deviceId}` : undefined}
                    onClick={() => onRemove(deviceId)}
                    className="text-gray-400 hover:text-red-500 transition-colors ml-0.5"
                    aria-label={`Remove ${deviceId}`}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceIdsViewer;
